import { FitLevel } from "@/enum/fit-level.enum";

import { People } from "../../../Apollo/schema/get-people";

export type DeepResearchResponseData = NonNullable<People["deepResearchResponse"]>;

export const FIT_LEVEL_LABELS: Record<FitLevel, string> = {
	[FitLevel.STRONG]: "Strong Fit",
	[FitLevel.MEDIUM]: "Medium Fit",
	[FitLevel.WEAK]: "Weak Fit",
	[FitLevel.NO_FIT]: "No Fit",
};

/**
 * Strips inline markdown syntax (bold, italics, code, links, bullet/heading
 * markers) so AI-generated markdown can be shown as a one-line plain preview.
 */
export const stripMarkdownInline = (text: string): string => {
	return text
		.replace(/^#{1,6}\s+/gm, "")
		.replace(/^\s*[-*+]\s+/gm, "")
		.replace(/\*\*(.+?)\*\*/g, "$1")
		.replace(/__(.+?)__/g, "$1")
		.replace(/\*([^*\n]+)\*/g, "$1")
		.replace(/_([^_\n]+)_/g, "$1")
		.replace(/`([^`]+)`/g, "$1")
		.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
};

export const formatSectionTitle = (key: string): string => {
	return key
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

export const formatDeepResearchAsText = (response: DeepResearchResponseData): string => {
	const lines: string[] = [];

	const fitLabel = response.fitAssessment
		? FIT_LEVEL_LABELS[response.fitAssessment as FitLevel]
		: undefined;

	if (fitLabel) {
		lines.push(`Fit Assessment: ${fitLabel}`);

		if (response.fitReason) {
			lines.push(`Reason: ${response.fitReason}`);
		}

		lines.push("");
	}

	const sections = Object.entries(response.response).sort(([, a], [, b]) => a.order - b.order);

	sections.forEach(([key, section]) => {
		lines.push(`${section.order}. ${formatSectionTitle(key)}`);

		if (section.content) {
			lines.push(section.content);
		}

		const sortedSubsections = section.subsections
			? [...section.subsections].sort((a, b) => a.order - b.order)
			: [];

		sortedSubsections.forEach((subsection) => {
			lines.push("");
			lines.push(subsection.header);
			lines.push(subsection.content);
		});

		lines.push("");
	});

	return lines.join("\n").trim();
};
